import { useState, useEffect } from 'react'
import {
  getChatbotVersions,
  createChatbotVersion,
  restoreChatbotVersion,
  ChatbotVersion,
} from '@/services/chatbot-config'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/hooks/use-toast'
import { Loader2, RotateCcw, Save } from 'lucide-react'
import { format } from 'date-fns'

export const VersionHistoryTab = () => {
  const [versions, setVersions] = useState<Omit<ChatbotVersion, 'snapshot'>[]>(
    [],
  )
  const [loading, setLoading] = useState(true)
  const [isCreating, setIsCreating] = useState(false)
  const [description, setDescription] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const { toast } = useToast()

  const fetchVersions = async () => {
    setLoading(true)
    const { data, error } = await getChatbotVersions()
    if (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load versions',
      })
    } else {
      setVersions(data || [])
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchVersions()
  }, [])

  const handleCreate = async () => {
    if (!description) return
    setIsCreating(true)
    try {
      const { error } = await createChatbotVersion(description)
      if (error) throw error
      toast({ title: 'Version created' })
      setIsDialogOpen(false)
      setDescription('')
      fetchVersions()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to create version',
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleRestore = async (versionId: string) => {
    if (
      !confirm('Are you sure? This will overwrite the current configuration.')
    )
      return

    try {
      const { error } = await restoreChatbotVersion(versionId)
      if (error) throw error
      toast({ title: 'Restored successfully' })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to restore version',
      })
    }
  }

  if (loading) return <Loader2 className="h-8 w-8 animate-spin mx-auto mt-10" />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Manage configuration snapshots to safely roll back changes.
        </p>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Save className="mr-2 h-4 w-4" /> Create Version
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Backup Version</DialogTitle>
              <DialogDescription>
                Save the current state of Knowledge Base and Dialogue Flow.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Label htmlFor="desc">Description</Label>
              <Input
                id="desc"
                placeholder="e.g. Before major update"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button
                onClick={handleCreate}
                disabled={isCreating || !description}
              >
                {isCreating && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Version
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {versions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  className="text-center py-8 text-muted-foreground"
                >
                  No versions saved yet.
                </TableCell>
              </TableRow>
            ) : (
              versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell className="font-medium">
                    {version.description}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(version.created_at), 'MMM dd, yyyy HH:mm')}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => handleRestore(version.id)}
                    >
                      <RotateCcw className="h-4 w-4" /> Restore
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
